import { DebugLog } from '../core/DebugLog';
import { getStorageAdapter } from '../core/StorageAdapter';
import type { ActionDef, QuestDef, QuestStepDef, QuestTriggerType } from '../types/data';

export type QuestStatus = 'active' | 'completed';
export type QuestProgress = {
  questId: string;
  status: QuestStatus;
  currentStepIndex: number;
  startedAt: string;
  updatedAt: string;
  completedAt?: string;
};
export type QuestEntry = {
  definition: QuestDef;
  progress: QuestProgress;
  currentStep?: QuestStepDef;
};
export type QuestUpdate = {
  questId: string;
  kind: 'started' | 'advanced' | 'completed';
  message: string;
};

type SavedQuestState = {
  version: 1;
  progress: Record<string, QuestProgress>;
};

const STORAGE_KEY = 'jrpg-launcher.quests.v1';
const storage = getStorageAdapter();
const listeners = new Set<(update: QuestUpdate) => void>();

export class QuestSystem {
  private static definitions = new Map<string, QuestDef>();
  private static progress = new Map<string, QuestProgress>();
  private static initialized = false;

  static initialize(definitions: QuestDef[]) {
    this.definitions = new Map(definitions.map((quest) => [quest.id, quest]));
    this.progress = this.loadProgress();
    this.initialized = true;

    definitions.filter((quest) => quest.autoStart).forEach((quest) => {
      if (!this.progress.has(quest.id)) this.start(quest.id, false);
    });
    DebugLog.info('QuestSystem', 'initialized', {
      definitions: definitions.length,
      progress: this.progress.size,
    });
  }

  static subscribe(listener: (update: QuestUpdate) => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  static getEntries(): QuestEntry[] {
    const entries: QuestEntry[] = [];
    this.progress.forEach((progress) => {
      const definition = this.definitions.get(progress.questId);
      if (!definition) return;
      entries.push({
        definition,
        progress,
        currentStep: definition.steps[progress.currentStepIndex],
      });
    });
    return entries.sort((a, b) => {
        if (a.progress.status !== b.progress.status) return a.progress.status === 'active' ? -1 : 1;
        if (a.definition.category !== b.definition.category) return a.definition.category === 'main' ? -1 : 1;
        return b.progress.updatedAt.localeCompare(a.progress.updatedAt);
      });
  }

  static start(questId: string, notify = true): string {
    const quest = this.requireQuest(questId);
    const existing = this.progress.get(questId);
    if (existing) return existing.status === 'completed' ? '이미 완료한 퀘스트입니다.' : '이미 진행 중인 퀘스트입니다.';

    const now = new Date().toISOString();
    const progress: QuestProgress = {
      questId,
      status: quest.steps.length ? 'active' : 'completed',
      currentStepIndex: 0,
      startedAt: now,
      updatedAt: now,
      completedAt: quest.steps.length ? undefined : now,
    };
    this.progress.set(questId, progress);
    this.saveProgress();
    const message = `새 퀘스트: ${quest.title}`;
    if (notify) this.emit({ questId, kind: 'started', message });
    return message;
  }

  static advance(questId: string, expectedStepId?: string): string {
    const quest = this.requireQuest(questId);
    const progress = this.progress.get(questId);
    if (!progress) return this.start(questId);
    if (progress.status === 'completed') return '이미 완료한 퀘스트입니다.';

    const currentStep = quest.steps[progress.currentStepIndex];
    if (expectedStepId && currentStep?.id !== expectedStepId) {
      return `현재 단계가 아니어서 진행하지 않았습니다: ${expectedStepId}`;
    }

    const nextIndex = progress.currentStepIndex + 1;
    if (nextIndex >= quest.steps.length) return this.complete(questId);

    progress.currentStepIndex = nextIndex;
    progress.updatedAt = new Date().toISOString();
    this.saveProgress();
    const message = `퀘스트 진행: ${quest.steps[nextIndex].title}`;
    this.emit({ questId, kind: 'advanced', message });
    return message;
  }

  static complete(questId: string): string {
    const quest = this.requireQuest(questId);
    let progress = this.progress.get(questId);
    if (progress?.status === 'completed') return '이미 완료한 퀘스트입니다.';
    if (!progress) {
      this.start(questId, false);
      progress = this.progress.get(questId);
    }
    if (!progress) return '퀘스트 진행 상태를 만들지 못했습니다.';

    const now = new Date().toISOString();
    progress.status = 'completed';
    progress.currentStepIndex = Math.max(0, quest.steps.length - 1);
    progress.updatedAt = now;
    progress.completedAt = now;
    this.saveProgress();
    const message = `퀘스트 완료: ${quest.title}`;
    this.emit({ questId, kind: 'completed', message });
    return message;
  }

  static record(triggerType: QuestTriggerType, targetId: string) {
    if (!this.initialized) return;
    const matching = this.getEntries().filter((entry) => {
      const trigger = entry.currentStep?.trigger;
      return entry.progress.status === 'active' && trigger?.type === triggerType && trigger.targetId === targetId;
    });
    matching.forEach((entry) => this.advance(entry.definition.id, entry.currentStep?.id));
  }

  static runAction(action: Extract<ActionDef, { type: 'quest_start' | 'quest_advance' | 'quest_complete' }>) {
    if (action.type === 'quest_start') return this.start(action.questId);
    if (action.type === 'quest_advance') return this.advance(action.questId, action.stepId);
    return this.complete(action.questId);
  }

  private static requireQuest(questId: string) {
    const quest = this.definitions.get(questId);
    if (!quest) throw new Error(`퀘스트를 찾을 수 없습니다: ${questId}`);
    return quest;
  }

  private static emit(update: QuestUpdate) {
    DebugLog.info('QuestSystem', update.message, { questId: update.questId, kind: update.kind });
    listeners.forEach((listener) => listener(update));
  }

  private static loadProgress() {
    try {
      const raw = storage.getItem(STORAGE_KEY);
      if (!raw) return new Map<string, QuestProgress>();
      const saved = JSON.parse(raw) as SavedQuestState;
      return new Map(Object.entries(saved.progress ?? {}));
    } catch (error) {
      DebugLog.warn('QuestSystem', 'saved progress could not be loaded', error);
      return new Map<string, QuestProgress>();
    }
  }

  private static saveProgress() {
    const progress = Object.fromEntries(this.progress);
    storage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, progress } satisfies SavedQuestState));
  }
}
